import { Button, CircularProgress, Grid, TextField } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  useCreateClientMutation,
  useUpdateClientMutation,
} from "../../../services/clients/clientsServices";
import { useEffect } from "react";
import { Cliente } from "../../../interfaces/clientes/cliente.interface";

interface props {
  clientToEdit?: Cliente;
  refetchList: () => void;
}

const AddClientForm = ({ clientToEdit, refetchList }: props) => {
  const mailsRegex = new RegExp(
    /^(([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+([;.](([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5}){1,25})+)*$/
  );

  const schema = yup.object().shape({
    nombre: yup.string().required("Nombre es requerido"),
    ruc: yup
      .string()
      .matches(/^[0-9]*$/, "Ingrese un valor correcto")
      .min(13, "Valor debe tener 13 digitos")
      .max(13, "Valor excede límite de digitos")
      .required("Ruc es requerido"),
    correos: yup
      .string()
      .matches(mailsRegex, "Error correos")
      .required("Correo/s es requerido"),
  });

  const [createClient, { isLoading, isError, isSuccess, reset, error }] =
    useCreateClientMutation();

  const [
    updateClient,
    {
      isLoading: isLoadingUpdate,
      isError: isErrorUpdate,
      isSuccess: isSuccessUpdate,
      reset: resetUpdate,
    },
  ] = useUpdateClientMutation();

  const {
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      nombre: clientToEdit?.nombre || "",
      ruc: clientToEdit?.ruc || "",
      correos: clientToEdit?.correos || "",
    },
    validationSchema: schema,
    onSubmit(values) {
      if (clientToEdit) {
        updateClient({
          id: clientToEdit.id!,
          nombre: values.nombre,
          ruc: values.ruc,
          correos: values.correos.length > 0 ? values.correos : undefined,
        });
      } else {
        createClient({
          nombre: values.nombre,
          ruc: values.ruc,
          correos: values.correos.length > 0 ? values.correos : undefined,
        });
      }
    },
  });

  useEffect(() => {
    if (isSuccess) {
      resetForm();
      refetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, resetForm]);

  useEffect(() => {
    if (isSuccessUpdate) {
      refetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessUpdate]);

  return (
    <Grid container spacing={2} direction="column">
      <Grid item xs={12} md={12}>
        <TextField
          {...getFieldProps("nombre")}
          fullWidth
          variant="outlined"
          placeholder="Ingrese nombre"
          label="Nombre"
          error={Boolean(errors.nombre && touched.nombre)}
          helperText={Boolean(errors.nombre && touched.nombre) && errors.nombre}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <TextField
          {...getFieldProps("ruc")}
          fullWidth
          variant="outlined"
          placeholder="Ingrese ruc"
          label="Ruc"
          error={Boolean(errors.ruc && touched.ruc)}
          helperText={Boolean(errors.ruc && touched.ruc) && errors.ruc}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <TextField
          {...getFieldProps("correos")}
          onChange={(e) => {
            setFieldValue("correos", String(e.target.value).toLowerCase());
          }}
          variant="outlined"
          fullWidth
          label="Correos"
          helperText="Uno o más correos (separados por punto y coma)"
          multiline
          minRows={4}
          error={Boolean(errors.correos && touched.correos)}
        />
      </Grid>
      {isSuccess && (
        <Grid item xs={12}>
          <Alert severity="success" onClose={reset}>
            Empresa creada correctamente
          </Alert>
        </Grid>
      )}
      {isError && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={reset}>
            {/* Ha ocurrido un error. Intente luego */}
            {String(error)}
          </Alert>
        </Grid>
      )}

      {isSuccessUpdate && (
        <Grid item xs={12}>
          <Alert severity="success" onClose={resetUpdate}>
            Empresa actualizada correctamente
          </Alert>
        </Grid>
      )}
      {isErrorUpdate && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={resetUpdate}>
            Ha ocurrido un error. Intente luego
          </Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <Button
          onClick={() => handleSubmit()}
          variant="contained"
          color="primary"
          disabled={isLoading || isLoadingUpdate}
          startIcon={
            isLoading || isLoadingUpdate ? (
              <CircularProgress size={10} />
            ) : (
              <IconDeviceFloppy />
            )
          }
        >
          guardar
        </Button>
      </Grid>
    </Grid>
  );
};

export default AddClientForm;
